// "The Metropolis API"

import { GraphQLClient } from 'graphql-request';
import { QueryClient } from '@tanstack/query-core';
import { getSdk } from './generated/gql-api.generated';

const createGQLClientV2 = (token?: string) => {
  return new GraphQLClient(
    process.env.NEXT_PUBLIC_AWS_APPSYNC_GRAPHQLENDPOINT_V2!,
    {
      headers: {
        'x-api-key': process.env.NEXT_PUBLIC_AWS_APPSYNC_APIKEY_V2!,
        authorization: `Bearer ${token}`,
      },
      requestMiddleware: (request) => {
        try {
          if (!token && localStorage) {
            request.headers['authorization'] = `Bearer ${localStorage.getItem(
              'ptoken'
            )}`;
          }

          return request;
        } catch (error) {
          return request;
        }
      },
      responseMiddleware: (response) => {
        if (!(response instanceof Error) && response.status === 200) {
          return response.data;
        }
        return response;
      },
    }
  );
};

export const {
  //GetAppChannelV2,
  GetChannelEventByStatusV2,
  GetCurrentUser,
  CheckEmail,
  CheckUsername,
  GetChannel,
  UpdateUserProfileV2,
  CreateCategoryV2,
  GetUserByUsername,
  GetStreamViewerV2,
  GetMediasForChannel,
  GetPresignedUploadUrlV2,
  GetSignedUrlV2,
  GetThumbnail,
  GetCategoriesV2,
  GetFeaturedCategoryByType,
  GetFeaturedCategoryByTypeLive,
  GetFeaturedCategoryByTypeRecent,
  UpdateCategoryV2,
  UpdateEventTypeV2,
  CreateEventTypeV2,
  GetEventTypesV2,
  CreateEventForChannelV2,
  GetEventsForChannel,
  GetAllChannels,
  getAllUsers,
  GetEventByIdV2,
  GetLandingDataV2,
  EditEventV2,
  UpdateEventChatOptionsV2,
  EventActionsV2,
  CreatorEventActionsV2,
  DeleteVideoById,
  GetEventsForCarouselV2,
  GetFeaturedEventV2,
  GetLiveEventsV2,
  GetLiveEventsV3,
  GetEventV2,
  GetUpcomingEventsV3,
  AddView,
  RemoveView,
  OnViewerNumberChange,
  SendMessageEventChatV2,
  PinMessageEventChatV2,
  SendMessageKlicChatV2,
  OnNewSendMessageEventChatV2,
  OnUpdateChatSettingV2,
  OnPinMessageEventChatV2,
  OnSendMessageKlicChatV2,
  GetEventChatMessagesV2,
  GetKlicChatMessagesV2,
  AttendEventV2,
  GetAttendeesV2,
  GlobalSignOutV2,
  CreateVisitEvent,
  CheckPPVVisit,
  StopStreamerByAdmin,
  OnStopStreamerByAdmin,
  StartStreamerByAdmin,
  OnStartStreamerByAdmin,
  EditMedia,
  EditCarousalV2,
  ModifyCarousalV2,
  ModifyFeatureCategoryPosition,
  UpdateFeaturedCategory,
  CreateFeaturedCategory,
  EditRecommendedEventV2,
  EditFeaturedEventV2,
  FolllowChannel,
  UnFolllowChannel,
  CheckFollow,
  DeleteMediaById,
  DeleteRecommendedEvent,
  DeleteCarouselEvent,
  DeleteFeaturedCategory,
  DeleteFeaturedEvent,
  SubscribeChannel,
  ChannelSubscription,
  UpdateAboutChannel,
  GetUserByEmail,
  GetEventViewers,
  DeleteEventType,
  DeleteCategory,
  GetSearchKeyword,
  CompleteSignUp,
  UpdateSocialLinkV2,
  GetMeAbout,
  GetLandingSidebarDataV2,
  GetCategoryById,
  GetCategoryByIdStatus,
  GetDashboardV2,
  GetFollowerListV2,
  GetFollowingListV2,
  GetPlanListV2,
  UpdatePlanV2,
  FolllowCategory,
  CheckCategoryFollow,
  GetCarouselEventsV2,
  GetCarouselEventsV3,
  GetAdminEventList,
  CongnitoProfileV2,
  AdminUserLockUnlockV2,
  DeleteUserById,
  UserProfileV2,
  DuplicateEventForChannel,
  FolllowChannelAction,
  CreateUserPanel,
  CreateEventPanel,
  CreateUserMasterPanel,
  UpdateMasterPanel,
  CreateEventMasterPanel,
  DeleteUserPanel,
  DeleteEventPanel,
  DeleteUserMasterPanel,
  DeleteEventMasterPanel,
  UpdateUserPanel,
  UpdateEventPanel,
  UpdatePanelPosition,
  UpdateCarouselPositionV2,
  UpdateFeaturedEventPosition,
  UpdateEventPanelPosition,
  UpdatePanelPositionByUserId,
  UpdatePanelPositionByEventId,
  GetPanelByUserId,
  GetPanelbyEventId,
  SetUserPreferenceV2,
  EditLandingFeatureCategoryV2,
  UpdateFeaturedCategoryPosition,
  GetSettingV2,
  GetChannelEventStreams,
  UpdateMinuteWatch,
  GetRevenueChart,
  GetChannelRevenueChart,
  ResetPasswordByAdmin,
  GetChatSetting,
  GetChatModerators,
  VerifyStreamAvailability,
  GetUserChatSettings,
  AddChatSetting,
  ContactRequestV2,
  UpdateChatSetting,
  AddUserChatSetting,
  UpdateUserChatSetting,
  AddChatModerator,
  DeleteChatModerator,
  ClearChatV2,
  GetTrendingCategory,
  GetCategoryWithScheduleEvents,
  GetCategoryWithEvents,
  GetNotificationHistory,
  GetPastEvents,
  GetCategoryWithEventsV2,
  GetChannelEventsByStatusByUsername,
  GetRoleManager,
  ModifyRoleManager,
  DeleteRoleManager,
  GetChatRuleByUserId,
  ModifyChatRuleByArray,
  ModifyPaymentSetting,
  GetPaymentSettingByUserId,
  GetPaymentHistory,
  DeletePaymentSettingById,
  GetReasonListForReport,
  ReportAndBlock,
  ReadNotification,
  RemoveMessageChat,
  NotifyResetPasswordUserId,
  CancelSubscription,
} = getSdk(createGQLClientV2());

const queryClientV2 = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

/*
 * TODO: We should be using `React.cache()` to make a request-scoped singleton out of queryClient. Unfortunately,
 * currently it's not supported because we get a "Not implemented" error during build time.
 */
export const getQueryClientV2 = (token?: string) => {
  const {
    GetChannelEventByStatusV2,
    GetCurrentUser,
    CheckEmail,
    CheckUsername,
    UpdateUserProfileV2,
    CreateCategoryV2,
    GetChannel,
    GetUserByUsername,
    GetStreamViewerV2,
    GetMediasForChannel,
    GetPresignedUploadUrlV2,
    GetSignedUrlV2,
    GetThumbnail,
    GetCategoriesV2,
    GetFeaturedCategoryByType,
    GetFeaturedCategoryByTypeLive,
    GetFeaturedCategoryByTypeRecent,
    UpdateCategoryV2,
    UpdateEventTypeV2,
    CreateEventTypeV2,
    GetEventTypesV2,
    CreateEventForChannelV2,
    GetEventsForChannel,
    GetAllChannels,
    getAllUsers,
    GetEventByIdV2,
    GetLandingDataV2,
    EditEventV2,
    UpdateEventChatOptionsV2,
    EventActionsV2,
    CreatorEventActionsV2,
    DeleteVideoById,
    GetEventsForCarouselV2,
    GetFeaturedEventV2,
    GetLiveEventsV2,
    GetLiveEventsV3,
    GetEventV2,
    GetUpcomingEventsV3,
    AddView,
    RemoveView,
    OnViewerNumberChange,
    SendMessageEventChatV2,
    PinMessageEventChatV2,
    SendMessageKlicChatV2,
    OnNewSendMessageEventChatV2,
    OnUpdateChatSettingV2,
    OnPinMessageEventChatV2,
    OnSendMessageKlicChatV2,
    GetEventChatMessagesV2,
    GetKlicChatMessagesV2,
    AttendEventV2,
    GetAttendeesV2,
    GlobalSignOutV2,
    CreateVisitEvent,
    CheckPPVVisit,
    StopStreamerByAdmin,
    OnStopStreamerByAdmin,
    StartStreamerByAdmin,
    OnStartStreamerByAdmin,
    EditMedia,
    EditCarousalV2,
    ModifyCarousalV2,
    ModifyFeatureCategoryPosition,
    UpdateFeaturedCategory,
    CreateFeaturedCategory,
    EditRecommendedEventV2,
    EditFeaturedEventV2,
    FolllowChannel,
    UnFolllowChannel,
    CheckFollow,
    DeleteMediaById,
    DeleteRecommendedEvent,
    DeleteCarouselEvent,
    DeleteFeaturedCategory,
    DeleteFeaturedEvent,
    SubscribeChannel,
    ChannelSubscription,
    UpdateAboutChannel,
    GetUserByEmail,
    GetSearchKeyword,
    GetEventViewers,
    DeleteEventType,
    DeleteCategory,
    CompleteSignUp,
    UpdateSocialLinkV2,
    GetMeAbout,
    GetLandingSidebarDataV2,
    GetCategoryById,
    GetCategoryByIdStatus,
    GetDashboardV2,
    GetFollowerListV2,
    GetFollowingListV2,
    GetPlanListV2,
    UpdatePlanV2,
    FolllowCategory,
    CheckCategoryFollow,
    GetCarouselEventsV2,
    GetCarouselEventsV3,
    GetAdminEventList,
    CongnitoProfileV2,
    AdminUserLockUnlockV2,
    DeleteUserById,
    UserProfileV2,
    DuplicateEventForChannel,
    FolllowChannelAction,
    CreateUserPanel,
    CreateEventPanel,
    CreateUserMasterPanel,
    UpdateMasterPanel,
    CreateEventMasterPanel,
    DeleteUserPanel,
    DeleteEventPanel,
    DeleteUserMasterPanel,
    DeleteEventMasterPanel,
    UpdateUserPanel,
    UpdateEventPanel,
    UpdatePanelPosition,
    UpdateCarouselPositionV2,
    UpdateFeaturedEventPosition,
    UpdateEventPanelPosition,
    UpdatePanelPositionByUserId,
    UpdatePanelPositionByEventId,
    GetPanelByUserId,
    GetPanelbyEventId,
    SetUserPreferenceV2,
    EditLandingFeatureCategoryV2,
    UpdateFeaturedCategoryPosition,
    GetSettingV2,
    GetChannelEventStreams,
    UpdateMinuteWatch,
    GetRevenueChart,
    GetChannelRevenueChart,
    ResetPasswordByAdmin,
    GetUserChatSettings,
    AddChatSetting,
    ContactRequestV2,
    UpdateChatSetting,
    AddUserChatSetting,
    UpdateUserChatSetting,
    AddChatModerator,
    DeleteChatModerator,
    ClearChatV2,
    GetChatSetting,
    GetChatModerators,
    VerifyStreamAvailability,
    GetTrendingCategory,
    GetCategoryWithScheduleEvents,
    GetCategoryWithEvents,
    GetNotificationHistory,
    GetPastEvents,
    GetCategoryWithEventsV2,
    GetChannelEventsByStatusByUsername,
    GetRoleManager,
    ModifyRoleManager,
    DeleteRoleManager,
    GetChatRuleByUserId,
    ModifyChatRuleByArray,
    ModifyPaymentSetting,
    GetPaymentSettingByUserId,
    GetPaymentHistory,
    DeletePaymentSettingById,
    GetReasonListForReport,
    ReportAndBlock,
    ReadNotification,
    RemoveMessageChat,
    NotifyResetPasswordUserId,
    CancelSubscription,
  } = getSdk(createGQLClientV2(token));
  return {
    GetChannelEventByStatusV2,
    queryClientV2,
    GetCurrentUser,
    CheckEmail,
    CheckUsername,
    GetChannel,
    UpdateUserProfileV2,
    CreateCategoryV2,
    GetUserByUsername,
    GetStreamViewerV2,
    GetMediasForChannel,
    GetPresignedUploadUrlV2,
    GetSignedUrlV2,
    GetThumbnail,
    GetCategoriesV2,
    GetFeaturedCategoryByType,
    GetFeaturedCategoryByTypeLive,
    GetFeaturedCategoryByTypeRecent,
    UpdateCategoryV2,
    UpdateEventTypeV2,
    CreateEventTypeV2,
    GetEventTypesV2,
    CreateEventForChannelV2,
    GetEventsForChannel,
    GetAllChannels,
    getAllUsers,
    GetEventByIdV2,
    GetLandingDataV2,
    EditEventV2,
    UpdateEventChatOptionsV2,
    EventActionsV2,
    CreatorEventActionsV2,
    DeleteVideoById,
    GetEventsForCarouselV2,
    GetFeaturedEventV2,
    GetLiveEventsV2,
    GetLiveEventsV3,
    GetEventV2,
    GetUpcomingEventsV3,
    AddView,
    RemoveView,
    OnViewerNumberChange,
    SendMessageEventChatV2,
    PinMessageEventChatV2,
    SendMessageKlicChatV2,
    OnNewSendMessageEventChatV2,
    OnUpdateChatSettingV2,
    OnPinMessageEventChatV2,
    OnSendMessageKlicChatV2,
    GetEventChatMessagesV2,
    GetKlicChatMessagesV2,
    AttendEventV2,
    GetAttendeesV2,
    GlobalSignOutV2,
    CreateVisitEvent,
    CheckPPVVisit,
    StopStreamerByAdmin,
    OnStopStreamerByAdmin,
    StartStreamerByAdmin,
    OnStartStreamerByAdmin,
    EditMedia,
    EditCarousalV2,
    ModifyCarousalV2,
    ModifyFeatureCategoryPosition,
    UpdateFeaturedCategory,
    CreateFeaturedCategory,
    EditRecommendedEventV2,
    EditFeaturedEventV2,
    FolllowChannel,
    UnFolllowChannel,
    CheckFollow,
    DeleteMediaById,
    DeleteRecommendedEvent,
    DeleteCarouselEvent,
    DeleteFeaturedCategory,
    DeleteFeaturedEvent,
    SubscribeChannel,
    ChannelSubscription,
    UpdateAboutChannel,
    GetUserByEmail,
    GetSearchKeyword,
    GetEventViewers,
    DeleteEventType,
    DeleteCategory,
    CompleteSignUp,
    UpdateSocialLinkV2,
    GetMeAbout,
    GetLandingSidebarDataV2,
    GetCategoryById,
    GetCategoryByIdStatus,
    GetDashboardV2,
    GetFollowerListV2,
    GetFollowingListV2,
    GetPlanListV2,
    UpdatePlanV2,
    FolllowCategory,
    CheckCategoryFollow,
    GetCarouselEventsV2,
    GetCarouselEventsV3,
    GetAdminEventList,
    CongnitoProfileV2,
    AdminUserLockUnlockV2,
    DeleteUserById,
    UserProfileV2,
    DuplicateEventForChannel,
    FolllowChannelAction,
    CreateUserPanel,
    CreateEventPanel,
    CreateUserMasterPanel,
    UpdateMasterPanel,
    CreateEventMasterPanel,
    DeleteUserPanel,
    DeleteEventPanel,
    DeleteUserMasterPanel,
    DeleteEventMasterPanel,
    UpdateUserPanel,
    UpdateEventPanel,
    UpdatePanelPosition,
    UpdateCarouselPositionV2,
    UpdateFeaturedEventPosition,
    UpdateEventPanelPosition,
    UpdatePanelPositionByUserId,
    UpdatePanelPositionByEventId,
    GetPanelByUserId,
    GetPanelbyEventId,
    SetUserPreferenceV2,
    EditLandingFeatureCategoryV2,
    UpdateFeaturedCategoryPosition,
    GetSettingV2,
    GetChannelEventStreams,
    UpdateMinuteWatch,
    GetRevenueChart,
    GetChannelRevenueChart,
    ResetPasswordByAdmin,
    GetUserChatSettings,
    AddChatSetting,
    ContactRequestV2,
    UpdateChatSetting,
    AddUserChatSetting,
    UpdateUserChatSetting,
    AddChatModerator,
    DeleteChatModerator,
    ClearChatV2,
    GetChatSetting,
    GetChatModerators,
    VerifyStreamAvailability,
    GetTrendingCategory,
    GetCategoryWithScheduleEvents,
    GetCategoryWithEvents,
    GetNotificationHistory,
    GetPastEvents,
    GetCategoryWithEventsV2,
    GetChannelEventsByStatusByUsername,
    GetRoleManager,
    ModifyRoleManager,
    DeleteRoleManager,
    GetChatRuleByUserId,
    ModifyChatRuleByArray,
    ModifyPaymentSetting,
    GetPaymentSettingByUserId,
    GetPaymentHistory,
    DeletePaymentSettingById,
    GetReasonListForReport,
    ReportAndBlock,
    ReadNotification,
    RemoveMessageChat,
    NotifyResetPasswordUserId,
    CancelSubscription,
  };
};
